
import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Friend } from "@/hooks/useFriends";
import { SearchUser } from "@/hooks/useUserSearch";

type UniversalUserData = Friend | SearchUser | {
  id: string;
  username: string;
  first_name: string | null;
  last_name: string | null;
  profile_picture: string | null;
  friend_profile?: {
    id: string;
    username: string;
    first_name: string | null;
    last_name: string | null;
    profile_picture: string | null;
  };
  friendship_status?: "none" | "pending_sent" | "pending_received" | "friends" | "rejected";
};

interface UniversalUserCardProps {
  user: UniversalUserData;
  variant: "friend" | "request" | "search";
  onAccept?: (friendshipId: string) => void;
  onReject?: (friendshipId: string) => void;
  onRemove?: (friendId: string) => void;
  onSendFriendRequest?: (userId: string) => void;
}

export const UniversalUserCard = ({ 
  user, 
  variant, 
  onAccept, 
  onReject, 
  onRemove, 
  onSendFriendRequest 
}: UniversalUserCardProps) => {
  const { t } = useTranslation('friends');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Extract user data based on variant
  const getUserData = () => {
    // For accepted friends and requests, prefer the friend's profile if present
    if ((variant === "friend" || variant === "request") && 'friend_profile' in user && user.friend_profile) {
      return {
        id: user.friend_profile.id,
        username: user.friend_profile.username,
        first_name: user.friend_profile.first_name,
        last_name: user.friend_profile.last_name,
        profile_picture: user.friend_profile.profile_picture,
      };
    }

    // For requests where the counterpart might be exposed as user_profile
    if (variant === "request" && 'user_profile' in user && (user as any).user_profile) {
      const up = (user as any).user_profile as {
        id: string;
        username: string;
        first_name: string | null;
        last_name: string | null;
        profile_picture: string | null;
      };
      return {
        id: up.id,
        username: up.username,
        first_name: up.first_name,
        last_name: up.last_name,
        profile_picture: up.profile_picture,
      };
    }
    
    // For search results or as a final fallback
    const userData = user as SearchUser;
    return {
      id: userData.id,
      username: userData.username,
      first_name: userData.first_name,
      last_name: userData.last_name,
      profile_picture: userData.profile_picture,
    };
  };

  const userData = getUserData();
  const displayName = [userData.first_name, userData.last_name].filter(Boolean).join(' ') || userData.username;

  const getInitials = () => {
    if (userData.first_name && userData.last_name) {
      return `${userData.first_name[0]}${userData.last_name[0]}`;
    }
    if (userData.first_name) return userData.first_name[0];
    if (userData.username) return userData.username[0].toUpperCase();
    return "U";
  };

  const handleRemoveFriend = () => {
    if (onRemove && variant === "friend") {
      onRemove(userData.id);
      setShowDeleteDialog(false);
    }
  };

  const renderFriendActions = () => (
    <div className="universal-user-card-actions">
      <Link to={`/timeline/${userData.username}`}>
        <Button variant="ghost" size="sm">
          View Timeline
        </Button>
      </Link>
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogTrigger asChild>
          <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
            {t('friends.actions.endFriendship')}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('friends.confirmDelete.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('friends.confirmDelete.description', { name: displayName })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('friends.confirmDelete.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemoveFriend} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t('friends.confirmDelete.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );

  const renderRequestActions = () => (
    <div className="universal-user-card-actions">
      <Button 
        size="sm" 
        onClick={() => onAccept && onAccept(user.id)}
        className="bg-green-600 hover:bg-green-700 text-white"
      >
        {t('requests.actions.accept')}
      </Button>
      <Button 
        variant="outline" 
        size="sm" 
        onClick={() => onReject && onReject(user.id)}
      >
        {t('requests.actions.reject')}
      </Button>
    </div>
  );

  const renderSearchActions = () => {
    const searchUser = user as SearchUser;
    
    const renderActionButton = () => {
      switch (searchUser.friendship_status) {
        case "friends":
          return (
            <Button variant="ghost" size="sm" className="text-green-600">
              {t('search.actions.friends')}
            </Button>
          );
        case "pending_sent":
          return (
            <Button variant="ghost" size="sm" disabled>
              {t('search.actions.requestSent')}
            </Button>
          );
        case "pending_received":
          return (
            <Button variant="ghost" size="sm" className="text-blue-600">
              {t('search.actions.requestReceived')}
            </Button>
          );
        case "rejected":
          return (
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => onSendFriendRequest && onSendFriendRequest(userData.id)}
              className="text-orange-600 border-orange-200 hover:bg-orange-50"
            >
              {t('search.actions.requestAgain')}
            </Button>
          );
        case "none":
        default:
          return (
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => onSendFriendRequest && onSendFriendRequest(userData.id)}
            >
              {t('search.actions.addFriend')}
            </Button>
          );
      }
    };

    return (
      <div className="universal-user-card-actions">
        <Link to={`/timeline/${userData.username}`}>
          <Button variant="ghost" size="sm">
            View Timeline
          </Button>
        </Link>
        {renderActionButton()}
      </div>
    );
  };

  const renderActions = () => {
    switch (variant) {
      case "friend":
        return renderFriendActions();
      case "request":
        return renderRequestActions();
      case "search":
        return renderSearchActions();
      default:
        return null;
    }
  };

  return (
    <Card className="universal-user-card uplivion-card">
      <CardContent className="universal-user-card-content">
        <div className="universal-user-card-avatar">
          <Avatar className="universal-user-card-avatar-image">
            <AvatarImage src={userData.profile_picture || undefined} />
            <AvatarFallback>{getInitials()}</AvatarFallback>
          </Avatar>
        </div>
        
        <div className="universal-user-card-info">
          <p className="universal-user-card-name">{displayName}</p>
          <p className="universal-user-card-username">@{userData.username}</p>
          {variant === "search" && (user as SearchUser).friendship_status === "rejected" && (
            <p className="universal-user-card-status">{t('search.status.rejected')}</p>
          )}
        </div>
        
        {renderActions()}
      </CardContent>
    </Card>
  );
};
